import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { userAuth } from '../services/userAuth';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await userAuth.login(email, password);
      const redirect = location.state?.from?.pathname || '/account';
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-gradient-to-b from-white to-slate-50" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#1a2e4e]" style={{ fontFamily: 'Chivo, sans-serif' }}>Welcome Back</h1>
          <p className="text-sm text-slate-500 mt-2">Sign in to view and manage your reservations.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="login-form">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1.5">Email</label>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20 focus:border-[#1a2e4e]/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1.5">Password</label>
            <input
              type="password" required value={password} onChange={e => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20 focus:border-[#1a2e4e]/40"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {loading ? 'Signing in…' : 'Sign In'}
          </Button>
          <p className="text-center text-sm text-slate-500 pt-2">
            New here? <Link to="/register" className="text-[#1a2e4e] font-semibold hover:underline" data-testid="login-to-register">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
